<template lang="pug">
  div
    //- Barra del grupo de instalaciones
    .home__title.media.media--center

      //- Datos del grupo de instalaciones
      .media__body
        span(v-if="!ACSMode") {{ $t('home.allUnitsAir') }}
        span(v-if="ACSMode") {{ $t('home.allUnitsACS') }}
        small ({{ devicesCount }})

      //- Botones
      .media__buttons(v-if="devicesCount")
        button.powerOff(
          v-if="!ACSMode"
          :class="{'powerOff--select': powerOn}"
          @click="swithOffAll")
        button.powerOn(
          v-if="!ACSMode"
          :class="{'powerOn--select': powerOn, 'powerOn--semi': powerOnSome}"
          @click="switchOnAll")
        button.powerful(
          v-if="ACSMode"
          :class="{'powerful--select': powerful}"
          @click="switchPowerfulAll")
          van-icon(name="powerful")

    //- Listado de instalaciones
    InstallationsRow(
      v-for="(installation, installationID) in installations"
      :key="installationID"
      :installationID="installationID"
      :ACSMode="ACSMode"
      @showInfo="showInfo")
</template>

<script>
import i18n from '@/locales/i18n';
import SocketService from '@/services/socket.service';
import DisableArcadeMixin from '@/mixins/DisableArcade.mixin';
import InstallationsRow from '@/components/Home/InstallationRow.vue';

export default {
  mixins: [DisableArcadeMixin],
  components: { InstallationsRow },
  props: {
    /**
     * Indica si la visualización es modo ACS
     */
    ACSMode: {
      type: Boolean,
      defautl: false,
    },

    /**
     * Listado de instalaciones
     */
    installations: {
      type: Object,
      required: true,
    },
  },
  computed: {
    /**
     * Devuelve el número de dispostivios del tipo indicado entre todas las instalaciones
     *
     * @return {Integer} - Número de dispositivos
     */
    devicesCount() {
      let devicesCount = 0;
      Object.keys(this.installations).forEach(installationID => {
        if (this.ACSMode) {
          Object.keys(this.installations[installationID].devices).forEach(mac => {
            if (this.installations[installationID].devices[mac].acs_available) {
              devicesCount += 1;
            }
          });
        } else {
          devicesCount += Object.keys(this.installations[installationID].devices).length;
        }
      });

      return devicesCount;
    },

    /**
     * Indica si todos los dispositivos del tipo indicado dentro de la instalación
     * están encendidos
     *
     * @return {Boolean} - Todos los dispostivos encendidos
     */
    powerOn() {
      if (this.ACSMode) return false;

      return Object.keys(this.installations).every(installationID => {
        return Object.keys(this.installations[installationID].devices).every(mac => {
          const device = this.installations[installationID].devices[mac];
          return device.power && device.machineready && device.isConnected && !device.error_value;
        });
      });
    },

    /**
     * Indica si aguno de los dispositivos del tipo indicado dentro de la instalación
     * están encendidos
     *
     * @return {Boolean} - Todos los dispostivos encendidos
     */
    powerOnSome() {
      if (this.ACSMode) return false;

      return Object.keys(this.installations).some(installationID => {
        return Object.keys(this.installations[installationID].devices).some(mac => {
          const device = this.installations[installationID].devices[mac];
          return device.power && device.machineready && device.isConnected && !device.error_value;
        });
      });
    },

    /**
     * Indica si todos los dispositivos cuando se visualizan como ACS
     * están trabajando a máxima potencia
     *
     * @return {Boolean} - Todos los dispostivos a máxima potencia
     */
    powerful() {
      if (!this.ACSMode) return false;

      return Object.keys(this.installations).some(installationID => {
        return Object.keys(this.installations[installationID].devices).every(mac => {
          const device = this.installations[installationID].devices[mac];

          // Si no se trata de un dispositivo Altherma, no lo tengo en cuenta
          if (!device.acs_available) return true;

          return (
            device.power_hot_water &&
            device.machineready &&
            device.isConnected &&
            !device.error_value
          );
        });
      });
    },
  },
  methods: {
    /**
     * Enciende todos los dispositivos
     */
    switchOnAll() {
      if (this.powerOn) return;
      this.setPropertyAll('power', true);
    },

    /**
     * Apaga todos los dispositivos
     */
    swithOffAll() {
      this.setPropertyAll('power', false);
    },

    /**
     * Pone a todos los dispositivos en modo máxima potencia
     */
    switchPowerfulAll() {
      this.setPropertyAll('power_hot_water', !this.powerful);
    },

    /**
     * Envía un evento con el nuevo valor de la propiedad
     * por cada dispositivo de la instalación
     *
     * @param {String} property - Propiedad que se quiere modificar
     * @param {String} value - Valor de la propiedad
     */
    setPropertyAll(property, value) {
      try {
        this.DisableArcadeMixin(() => {
          /**
           * Recorre todos los dispositivos
           */
          Object.keys(this.installations).forEach(installationID => {
            Object.keys(this.installations[installationID].devices).forEach(mac => {
              if (
                (value && this.installations[installationID].devices[mac].block_on) ||
                (!value && this.installations[installationID].devices[mac].block_off) ||
                (this.installations[installationID].devices[mac].link_fallback &&
                  !this.installations[installationID].devices[mac].link_isConnected) ||
                !this.installations[installationID].devices[mac].isConnected ||
                !this.installations[installationID].devices[mac].machineready ||
                this.installations[installationID].devices[mac].error_value
              ) {
                return;
              }
              const data = { mac, property, value };
              SocketService.setDeviceValue(installationID, data);
            });
          });
        });
      } catch (error) {
        this.$toast.fail({
          mask: true,
          message: i18n.t('general.errors.default.message'),
        });
      }
    },

    /**
     * Envía un evento para mostrar la información del dispositivo
     */
    showInfo(data) {
      this.$emit('showInfo', data);
    },
  },
};
</script>
